import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, SystemBars, SystemBarsStyle, type PluginListenerHandle } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

import type { Prefs, UserInfo } from '../types/book';
import { ExternalNavigation } from '../plugins/externalNavigation';
import { IncomingFiles, type PendingNativeFile } from '../plugins/incomingFiles';
import { NativeDownloads } from '../plugins/nativeDownloads';
import { logger } from './logger';
import { getAndroidPlayStoreUpdateUrls, normalizeAndroidPlayStoreUrl } from './storeUpdates';

export type AppPlatform = 'web' | 'ios' | 'android';
export type SafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

const DEFAULT_NATIVE_API_ORIGIN = 'https://wereader.app';
const NATIVE_FETCH_PREFIXES = ['/api/', '/auth/mobile/'];
const originalFetch = globalThis.fetch.bind(globalThis);

let fetchBridgeInstalled = false;

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');
const NATIVE_DOCUMENT_CLASSES = ['native-app', 'native-ios', 'native-android', 'native-immersive'];
const ANDROID_DOWNLOAD_CHUNK_SIZE_BYTES = 4 * 1024 * 1024;

const normalizeRelativeUrl = (url: string): string => {
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    return url;
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  if (url.startsWith('/')) {
    return url;
  }

  return `/${url}`;
};

const shouldRewriteNativeRequest = (url: string): boolean =>
  NATIVE_FETCH_PREFIXES.some((prefix) => url.startsWith(prefix));

const resolveSystemBarsStyle = (effectiveTheme?: 'light' | 'dark'): SystemBarsStyle => {
  if (effectiveTheme === 'light') {
    return SystemBarsStyle.Light;
  }

  if (effectiveTheme === 'dark') {
    return SystemBarsStyle.Dark;
  }

  return SystemBarsStyle.Default;
};

const syncNativeDocumentClasses = (immersiveMode: boolean): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const platform = getAppPlatform();
  const targets = [document.documentElement, document.body].filter(
    (target): target is HTMLElement => Boolean(target),
  );

  targets.forEach((target) => {
    target.classList.remove(...NATIVE_DOCUMENT_CLASSES);
    target.classList.add('native-app');
    target.classList.add(platform === 'ios' ? 'native-ios' : 'native-android');

    if (immersiveMode) {
      target.classList.add('native-immersive');
    }
  });
};

const toNativeApiUrl = (url: string): string => {
  const normalized = normalizeRelativeUrl(url);
  return shouldRewriteNativeRequest(normalized) ? resolveServerUrl(normalized) : normalized;
};

const toReadableError = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error);

const blobToBase64 = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob as base64'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result while encoding blob'));
        return;
      }

      const [, base64Payload = ''] = result.split(',', 2);
      resolve(base64Payload);
    };
    reader.readAsDataURL(blob);
  });

const sanitizeFileName = (fileName: string): string =>
  fileName.replace(/[^a-zA-Z0-9._-]/g, '-').replace(/-+/g, '-');

const readInsetValue = (
  style: CSSStyleDeclaration,
  propertyName: string,
): number => parseInt(style.getPropertyValue(propertyName), 10) || 0;

const isNativeEpub = (file: PendingNativeFile): boolean => {
  const normalizedMimeType = file.mimeType?.toLowerCase() ?? '';
  const normalizedName = file.name?.toLowerCase() ?? '';
  return (
    normalizedMimeType === 'application/epub+zip' ||
    normalizedMimeType === 'application/epub' ||
    normalizedName.endsWith('.epub')
  );
};

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

export const getAppPlatform = (): AppPlatform => {
  if (!isNativeApp()) {
    return 'web';
  }

  return Capacitor.getPlatform() as Exclude<AppPlatform, 'web'>;
};

export const getNativeApiOrigin = (): string =>
  trimTrailingSlash(import.meta.env.VITE_NATIVE_API_ORIGIN || DEFAULT_NATIVE_API_ORIGIN);

export const resolveServerUrl = (path: string): string => new URL(normalizeRelativeUrl(path), `${getNativeApiOrigin()}/`).toString();

export const readDocumentSafeAreaInsets = (): SafeAreaInsets => {
  if (typeof document === 'undefined') {
    return {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    top: readInsetValue(style, '--app-safe-top'),
    bottom: readInsetValue(style, '--app-safe-bottom'),
    left: readInsetValue(style, '--app-safe-left'),
    right: readInsetValue(style, '--app-safe-right'),
  };
};

export const supportsNativeImmersiveMode = (): boolean => getAppPlatform() === 'android';

export const getNativeImmersiveModeEnabled = (nativeImmersiveMode?: Prefs['nativeImmersiveMode']): boolean =>
  supportsNativeImmersiveMode() && nativeImmersiveMode !== false;

export const installNativeFetchBridge = (): void => {
  if (fetchBridgeInstalled || !isNativeApp() || typeof window === 'undefined') {
    return;
  }

  fetchBridgeInstalled = true;
  logger.info('Installing native fetch bridge', {
    nativeApiOrigin: getNativeApiOrigin(),
    platform: getAppPlatform(),
  });

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string' || input instanceof URL) {
      const rewrittenUrl = toNativeApiUrl(input.toString());
      const nextInit =
        rewrittenUrl === input.toString()
          ? init
          : {
              ...init,
              credentials: init?.credentials ?? 'include',
            };
      return originalFetch(rewrittenUrl, nextInit);
    }

    return originalFetch(input, init);
  }) as typeof window.fetch;
};

export const apiFetch = (input: string, init?: RequestInit): Promise<Response> => {
  const url = isNativeApp() ? toNativeApiUrl(input) : input;
  return fetch(url, {
    ...init,
    credentials: init?.credentials ?? 'include',
  });
};

export const startLogin = async (webAuthUrl: string): Promise<void> => {
  if (!isNativeApp()) {
    window.location.href = webAuthUrl;
    return;
  }

  const mobileAuthUrl = resolveServerUrl(`/auth/mobile/start?platform=${encodeURIComponent(getAppPlatform())}`);
  logger.info('Opening native login flow', {
    mobileAuthUrl,
  });
  await Browser.open({
    url: mobileAuthUrl,
  });
};

export const completeNativeSessionTicket = async (ticket: string): Promise<UserInfo | null> => {
  const response = await apiFetch('/api/mobile/session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ticket }),
  });

  if (!response.ok) {
    logger.error('Native session ticket exchange failed', {
      status: response.status,
      ticketPreview: ticket.slice(0, 8),
    });
    return null;
  }

  const payload = await response.json();
  if (!payload?.email) {
    logger.warn('Native session ticket exchange completed without user payload', payload);
    return null;
  }

  return payload as UserInfo;
};

export const openExternalUrl = async (url: string, options?: { replace?: boolean }): Promise<void> => {
  const normalizedUrl =
    isNativeApp() && !/^[a-z][a-z0-9+.-]*:/i.test(url) ? resolveServerUrl(url) : url;

  if (isNativeApp()) {
    if (!/^https?:/i.test(normalizedUrl)) {
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    await Browser.open({ url: normalizedUrl });
    return;
  }

  if (options?.replace) {
    window.location.replace(normalizedUrl);
    return;
  }

  window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
};

export const openNativeAndroidStoreUpdate = async (
  storeUrl?: string | null,
): Promise<{ opened: boolean; url: string | null }> => {
  const normalizedStoreUrl = normalizeAndroidPlayStoreUrl(storeUrl);
  const candidateUrls = getAndroidPlayStoreUpdateUrls(storeUrl);

  logger.info('Preparing Android store update launch', {
    requestedStoreUrl: storeUrl ?? '(none)',
    normalizedStoreUrl: normalizedStoreUrl ?? '(fallback)',
    candidateUrls,
  });

  if (getAppPlatform() !== 'android') {
    const fallbackUrl = normalizedStoreUrl ?? candidateUrls.find((candidateUrl) => /^https?:/i.test(candidateUrl)) ?? null;
    if (!fallbackUrl) {
      logger.warn('No fallback store URL is available for non-Android platform', {
        requestedStoreUrl: storeUrl ?? '(none)',
      });
      return { opened: false, url: null };
    }

    await openExternalUrl(fallbackUrl);
    return { opened: true, url: fallbackUrl };
  }

  for (const candidateUrl of candidateUrls) {
    try {
      await ExternalNavigation.openUrl({ url: candidateUrl });
      logger.info('Opened Android store update URL', {
        candidateUrl,
        requestedStoreUrl: storeUrl ?? '(none)',
      });
      return { opened: true, url: candidateUrl };
    } catch (error) {
      logger.warn('Failed to open Android store update URL', {
        candidateUrl,
        requestedStoreUrl: storeUrl ?? '(none)',
        error: toReadableError(error),
      });
    }
  }

  logger.warn('Unable to open any Android store update URL', {
    requestedStoreUrl: storeUrl ?? '(none)',
    candidateUrls,
  });
  return { opened: false, url: null };
};

export const closeAuthBrowser = async (): Promise<void> => {
  if (!isNativeApp()) {
    return;
  }

  try {
    await Browser.close();
  } catch (error) {
    logger.debug('Ignoring Browser.close() failure during native auth cleanup', {
      error: toReadableError(error),
    });
  }
};

export const getNativeLaunchUrl = async (): Promise<string | null> => {
  if (!isNativeApp()) {
    return null;
  }

  try {
    const launchResult = await App.getLaunchUrl();
    return launchResult?.url ?? null;
  } catch (error) {
    logger.warn('Failed to read native launch URL', {
      error: toReadableError(error),
    });
    return null;
  }
};

export const addNativeUrlOpenListener = async (
  listener: (url: string) => void | Promise<void>,
): Promise<PluginListenerHandle | null> => {
  if (!isNativeApp()) {
    return null;
  }

  return App.addListener('appUrlOpen', (event) => {
    void listener(event.url);
  });
};

export const addNativeAppStateListener = async (
  listener: (isActive: boolean) => void | Promise<void>,
): Promise<PluginListenerHandle | null> => {
  if (!isNativeApp()) {
    return null;
  }

  return App.addListener('appStateChange', (event) => {
    void listener(event.isActive);
  });
};

export const addAndroidBackButtonListener = async (
  listener: () => void | Promise<void>,
): Promise<PluginListenerHandle | null> => {
  if (getAppPlatform() !== 'android') {
    return null;
  }

  return App.addListener('backButton', () => {
    void listener();
  });
};

export const exitNativeApp = async (): Promise<void> => {
  if (getAppPlatform() !== 'android') {
    return;
  }

  await App.exitApp();
};

export const configureNativeChrome = async (options?: {
  effectiveTheme?: 'light' | 'dark';
  nativeImmersiveMode?: Prefs['nativeImmersiveMode'];
}): Promise<void> => {
  if (!isNativeApp()) {
    return;
  }

  const immersiveMode = getNativeImmersiveModeEnabled(options?.nativeImmersiveMode);
  syncNativeDocumentClasses(immersiveMode);

  try {
    await SystemBars.setStyle({
      style: resolveSystemBarsStyle(options?.effectiveTheme),
    });

    if (supportsNativeImmersiveMode()) {
      if (immersiveMode) {
        await SystemBars.hide();
      } else {
        await SystemBars.show();
      }
      return;
    }

    await SystemBars.show();
  } catch (error) {
    logger.warn('Failed to configure native system bars', {
      effectiveTheme: options?.effectiveTheme ?? '(default)',
      immersiveMode,
      platform: getAppPlatform(),
      error: toReadableError(error),
    });
  }
};

export const consumePendingNativeEpubFiles = async (): Promise<File[]> => {
  if (!isNativeApp()) {
    return [];
  }

  try {
    const { files } = await IncomingFiles.consumePendingFiles();
    if (!Array.isArray(files) || files.length === 0) {
      return [];
    }

    const pendingFiles = files.filter(isNativeEpub);
    logger.info('Consuming pending native EPUB files', {
      platform: getAppPlatform(),
      receivedCount: files.length,
      epubCount: pendingFiles.length,
    });

    const resolvedFiles = await Promise.all(
      pendingFiles.map(async (file) => {
        const fileUrl = Capacitor.convertFileSrc(file.uri);
        const response = await originalFetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to read native file ${file.name}: ${response.status}`);
        }

        const blob = await response.blob();
        return new File([blob], file.name, {
          type: file.mimeType || 'application/epub+zip',
          lastModified: Date.now(),
        });
      }),
    );

    return resolvedFiles;
  } catch (error) {
    logger.error('Failed to consume pending native EPUB files', {
      error: toReadableError(error),
    });
    return [];
  }
};

export const shareTextContent = async (title: string, text: string): Promise<boolean> => {
  if (isNativeApp()) {
    await Share.share({ title, text });
    return true;
  }

  if (!navigator.share) {
    return false;
  }

  await navigator.share({ title, text });
  return true;
};

export const shareBlobFile = async (params: {
  blob: Blob;
  filename: string;
  title: string;
  text: string;
}): Promise<boolean> => {
  const { blob, filename, title, text } = params;

  if (isNativeApp()) {
    const sharePath = `shares/${Date.now()}-${sanitizeFileName(filename)}`;
    const base64Data = await blobToBase64(blob);
    const writeResult = await Filesystem.writeFile({
      path: sharePath,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });
    try {
      const statResult = await Filesystem.stat({
        path: sharePath,
        directory: Directory.Cache,
      });
      logger.info('Prepared native share file in cache', {
        filename,
        sharePath,
        uri: writeResult.uri,
        blobSize: blob.size,
        cachedSize: statResult.size ?? null,
        mimeType: blob.type || 'application/octet-stream',
      });
    } catch (error) {
      logger.warn('Failed to stat native share file after cache write', {
        filename,
        sharePath,
        uri: writeResult.uri,
        blobSize: blob.size,
        error: toReadableError(error),
      });
    }
    await Share.share({
      title,
      text,
      files: [writeResult.uri],
    });
    return true;
  }

  if (!navigator.share) {
    return false;
  }

  const file = new File([blob], filename, {
    type: blob.type || 'application/octet-stream',
  });
  await navigator.share({
    title,
    text,
    files: [file],
  });
  return true;
};

export const saveBlobFileToAndroidDownloads = async (params: {
  blob: Blob;
  filename: string;
  onProgress?: (progress: {
    saveId: string;
    chunkIndex: number;
    totalChunks: number;
    uploadedBytes: number;
    totalBytes: number;
    nativeByteCount: number;
  }) => void;
}): Promise<boolean> => {
  const { blob, filename, onProgress } = params;
  if (getAppPlatform() !== 'android') {
    return false;
  }
  console.debug('Saving blob file to Android Downloads', {
    filename,
    blobSize: blob.size,
    mimeType: blob.type || 'application/octet-stream',
    chunkSizeBytes: ANDROID_DOWNLOAD_CHUNK_SIZE_BYTES,
  });

  let saveId: string | null = null;
  try {
    const startResult = await NativeDownloads.startSave({
      filename,
      mimeType: blob.type || 'application/octet-stream',
    });
    saveId = startResult.saveId;

    let uploadedBytes = 0;
    let chunkIndex = 0;
    const totalChunks = Math.max(1, Math.ceil(blob.size / ANDROID_DOWNLOAD_CHUNK_SIZE_BYTES));
    console.debug('Started Android download save', {
      filename,
      saveId,
      totalChunks,
      blobSize: blob.size,
    });
    onProgress?.({
      saveId,
      chunkIndex: 0,
      totalChunks,
      uploadedBytes: 0,
      totalBytes: blob.size,
      nativeByteCount: startResult.byteCount,
    });

    while (uploadedBytes < blob.size) {
      const nextChunkEnd = Math.min(uploadedBytes + ANDROID_DOWNLOAD_CHUNK_SIZE_BYTES, blob.size);
      const chunkBlob = blob.slice(uploadedBytes, nextChunkEnd);
      const base64Data = await blobToBase64(chunkBlob);
      const chunkResult = await NativeDownloads.appendChunk({
        saveId,
        base64Data,
      });

      uploadedBytes = nextChunkEnd;
      chunkIndex += 1;
      onProgress?.({
        saveId,
        chunkIndex,
        totalChunks,
        uploadedBytes,
        totalBytes: blob.size,
        nativeByteCount: chunkResult.byteCount,
      });

      if (
        chunkIndex === 1 ||
        chunkIndex === totalChunks ||
        chunkIndex % 8 === 0
      ) {
        console.debug('Appended Android download chunk', {
          filename,
          saveId,
          chunkIndex,
          totalChunks,
          uploadedBytes,
          nativeByteCount: chunkResult.byteCount,
          blobSize: blob.size,
        });
      }
    }

    const result = await NativeDownloads.finishSave({
      saveId,
    });

    console.debug('Saved blob file to Android Downloads', {
      filename,
      saveId,
      savedFilename: result.filename,
      byteCount: result.byteCount,
      uri: result.uri,
      location: result.location,
      totalChunks,
    });
    return true;
  } catch (error) {
    console.debug('Failed while streaming blob file to Android Downloads', {
      filename,
      saveId: saveId ?? '(none)',
      blobSize: blob.size,
      error: toReadableError(error),
    });

    if (saveId) {
      try {
        await NativeDownloads.cancelSave({ saveId });
      } catch (cancelError) {
        console.debug('Failed to cancel partial Android download after save failure', {
          filename,
          saveId,
          error: toReadableError(cancelError),
        });
      }
    }

    throw error;
  }
};
